import FilterListIcon from "@mui/icons-material/FilterList";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { Category } from "config";
import { FilterChip, ArrayChip, RangeChip, TextChip } from "marketplace/_views/filters/filter-chip";
import { cloneFilterChips, FilterChips } from "marketplace/_views/filters/filter-chips";
import Results from "marketplace/_views/results";
import SearchBar from "marketplace/_views/search-bar";
import SortButton from "marketplace/_views/sort-button";
import {
  MarketplaceSortValue,
  MarketplaceSorts,
  normalizeMarketplaceSort,
} from 'marketplace/search/marketplace-sorts';
import Individual from "marketplace/individuals/individual";
import { SelectionFilters } from "marketplace/search/marketplace-search-filters";
import Team from "marketplace/teams/team";
import React from "react";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import FilterList from "marketplace/_views/filters/filter-list";

const MainContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  position: "relative",
  width: "100%",
}));
const SearchContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  backgroundColor: "antiquewhite",
  borderRadius: compactLayout ? 0 : theme.spacing(0.5),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3.5),
  paddingRight: theme.spacing(3.5),
  paddingTop: theme.spacing(2),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflowY: "auto",
  width: "100%",
}));
const ResultsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginTop: 0,
  },
  width: "100%",
}));
const ResultsPane = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    overflowX: "hidden",
    overflowY: "visible",
    padding: theme.spacing(0),
  },
  backgroundColor: "#FAFAFA",
  flex: 1,
  minWidth: 0,
  padding: theme.spacing(0, 1),
  width: "100%",
}));
const ChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(1),
  },
}));
const SearchFilterChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
}));
const TopBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  backdropFilter: "blur(7px) saturate(200%)",
  backgroundColor: "rgba(250, 250, 250, 0.87)",
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
  position: "sticky",
  top: 0,
  zIndex: 10,
}));
const FilterButtons = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(1),
  },
}));
const ShowResultsButton = styled(Button)(() => ({
  display: "block",
  margin: "auto",
}));
const ContainedFilterPanel = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  left: 0,
  minHeight: 0,
  overflow: "hidden",
  position: "absolute",
  right: 0,
  top: 0,
  zIndex: 30,
}));
const ContainedFilterPanelTitle = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3, 1, 1),
  position: "sticky",
  top: 0,
  zIndex: 1,
}));
const ContainedFilterPanelContent = styled("div")(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
}));

type MarketplaceProps = {
  selectingLeader?: boolean;
  selectingMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  searchParams?: URLSearchParams;
  navigate?: NavigateFunction;
  filterPresentation?: "dialog" | "contained";
  onContainedFilterOpenChange?: (isOpen: boolean) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
};

export default function Marketplace(props: Readonly<MarketplaceProps>) {
  const {
    selectingLeader,
    selectingMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    searchParams,
    navigate,
    onContainedFilterOpenChange,
    onTeamMemberSelected,
    onTeamSelected,
  } = props;
  const filterPresentation = props.filterPresentation ?? "dialog";

  const resultsRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const createInitialFilters = () => {
    const initialFilters = new FilterChips(cloneFilterChips(SelectionFilters));
    return initialFilters;
  };
  const filtersRef = React.useRef<FilterChips>(createInitialFilters());

  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState<FilterChips>(
    () => new FilterChips(cloneFilterChips(SelectionFilters)),
  );
  const [sortBy, setSortBy] = React.useState<MarketplaceSortValue>(
    MarketplaceSorts.Relevance,
  );
  const [category, setCategory] = React.useState("all");
  const [numResults, setNumResults] = React.useState(0);
  const [containedFilterPanelOpen, setContainedFilterPanelOpen] = React.useState(false);
  const [filterPopoverAnchor, setFilterPopoverAnchor] =
    React.useState<HTMLButtonElement | null>(null);

  const { openDialog, closeDialog } = useDialog();
  const session = useSession();
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const isMediumContainer = containerWidth >= 900;
  const isCompactContainer = containerWidth > 0 && containerWidth < 1200;

  React.useEffect(() => {
    onContainedFilterOpenChange?.(containedFilterPanelOpen);
  }, [containedFilterPanelOpen, onContainedFilterOpenChange]);

  React.useEffect(() => {
    return () => onContainedFilterOpenChange?.(false);
  }, [onContainedFilterOpenChange]);

  React.useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  function handleFilterChanged(updatedFilter: FilterChip) {
    if (!updatedFilter.value) return;

    const targetChip = filtersRef.current.findByName(updatedFilter.filterName);

    if (!targetChip) return;

    if (targetChip instanceof ArrayChip || targetChip instanceof RangeChip) {
      filtersRef.current.updateValue(
        updatedFilter.filterName,
        updatedFilter.value as string[]
      );
    } else {
      filtersRef.current.updateValue(
        updatedFilter.filterName,
        updatedFilter.value as string
      );
    }

    setFilters(new FilterChips(filtersRef.current.chips));
  }

  function handleFilterEnabled(updatedFilter: FilterChip) {
    filtersRef.current.enable(updatedFilter.filterName);
    setFilters(new FilterChips(filtersRef.current.chips));
  }

  function handleFilterDisabled(updatedFilter: FilterChip) {
    filtersRef.current.disable(updatedFilter.filterName);
    setFilters(new FilterChips(filtersRef.current.chips));

    if (updatedFilter.filterName === "query") {
      setSearchTerm("");
    }
  }

  function handleCategoryChanged(category: Category) {
    setNumResults(0);
    setSearchTerm("");
    setCategory(category.id);
    setSortBy(MarketplaceSorts.Relevance);

    filtersRef.current = new FilterChips(cloneFilterChips(SelectionFilters));

    if (category.id !== "all") {
      filtersRef.current.enable(category.id);
      const updatedFilter = new TextChip("category", category.name);
      handleFilterChanged(updatedFilter);
    }

    setFilters(new FilterChips(filtersRef.current.chips));
  }

  function handleSearch(term: string = "") {
    setSearchTerm(term);
    filtersRef.current.updateValue("query", term);
    filtersRef.current.enable("query");
    setFilters(new FilterChips(filtersRef.current.chips));
  }

  function handleSort(sortField: MarketplaceSortValue) {
    setSortBy(normalizeMarketplaceSort(sortField));
  }

  function renderFilterList() {
    return (
      <FilterList
        dialog={true}
        filters={filters}
        searchParams={searchParams}
        onChange={handleFilterChanged}
        onAdded={handleFilterEnabled}
        onRemoved={handleFilterDisabled}
      />
    );
  }

  function openFilterDialog() {
    if (filterPresentation === "contained") {
      setContainedFilterPanelOpen(true);
      return;
    }

    const dialogProps: DialogProps = {
      MuiProps: {
        fullScreen: true,
        keepMounted: true,
        disablePortal: true,
      },
      title: (
        <ShowResultsButton
          size="large"
          variant="contained"
          disableElevation={true}
          color="primary"
          onClick={() => closeDialog()}
        >
          Show {numResults} Result{numResults === 1 ? null : "s"}
        </ShowResultsButton>
      ),
      component: renderFilterList(),
    };
    openDialog(dialogProps);
  }

  return (
    <MainContainer ref={containerRef}>
      <SearchContainer compactLayout={isCompactContainer}>
        <SearchBar
          embedded={true}
          searchParams={searchParams}
          searchTerm={searchTerm}
          onCategoryChanged={handleCategoryChanged}
          onSearch={handleSearch}
          onSearchCleared={() => {
            handleFilterDisabled(filtersRef.current.findByName("query")!);
            handleSearch();
          }}
        />
      </SearchContainer>
      <MainContent>
        <ResultsPane ref={resultsRef}>
          <TopBar>
            <ChipContainer>
              {filters.chips.every((chip: FilterChip) => !chip.enabled) && (
                <SearchFilterChip
                  label="No Active Filters"
                  disabled
                  size="small"
                />
              )}
              {filters.chips.map((chip: FilterChip) => (
                <SearchFilterChip
                  key={chip.filterName}
                  label={chip.label}
                  size="small"
                  style={{ display: chip.enabled ? "inline-flex" : "none" }}
                  onDelete={() => handleFilterDisabled(chip)}
                />
              ))}
            </ChipContainer>
            <FilterButtons>
              <Button
                size="large"
                color="primary"
                startIcon={<FilterListIcon />}
                  onClick={(event) =>
                  isMediumContainer
                    ? setFilterPopoverAnchor(event.currentTarget)
                    : openFilterDialog()
                }
              >
                Filters
              </Button>
              <Popover
                anchorEl={filterPopoverAnchor}
                open={Boolean(filterPopoverAnchor)}
                onClose={() => setFilterPopoverAnchor(null)}
              >
                {renderFilterList()}
              </Popover>
              <SortButton sortBy={sortBy} onSort={handleSort} />
            </FilterButtons>
          </TopBar>
          <ResultsContainer>
            <Results
              session={session}
              embedded={true}
              navigate={navigate}
              selectingTeamLeader={selectingLeader}
              selectingTeamMember={selectingMember}
              selectedTeamMemberUserIds={selectedTeamMemberUserIds}
              selectingTeam={selectingTeam}
              category={category}
              searchTerm={searchTerm}
              filters={filters.toDictionary()}
              sortBy={sortBy}
              onResults={(results: number | undefined) =>
                setNumResults(results ?? 0)
              }
              onTeamMemberSelected={onTeamMemberSelected}
              onTeamSelected={onTeamSelected}
              compactLayout={isCompactContainer}
            />
          </ResultsContainer>
        </ResultsPane>
      </MainContent>
      {containedFilterPanelOpen && (
        <ContainedFilterPanel>
          <ContainedFilterPanelTitle>
            <ShowResultsButton
              size="large"
              variant="contained"
              disableElevation={true}
              color="primary"
              onClick={() => setContainedFilterPanelOpen(false)}
            >
              Show {numResults} Result{numResults === 1 ? null : "s"}
            </ShowResultsButton>
          </ContainedFilterPanelTitle>
          <ContainedFilterPanelContent>{renderFilterList()}</ContainedFilterPanelContent>
        </ContainedFilterPanel>
      )}
    </MainContainer>
  );
}
